/*
 * Copyright (c) 2023 Bluemate B.V.
 */
import './style/passwordSettings.scss';

import React, {FocusEventHandler, MouseEventHandler} from 'react';

import {useTranslation} from 'react-i18next';

import {ChangeFunction, Loader} from '@dvrd/dvr-controls';

import {PasswordErrors} from '../../../util/interfaces';

import PasswordStrength from '../../password/passwordStrength';

import {BluemateButton, BluematePasswordInput} from '../../controls';

import BluemateSwitch from '../../controls/switch/bluemateSwitch';

import InfoBanner from '../../misc/infoBanner';

interface Props {
    onChange: (key: 'new' | 'confirm' | 'pass') => ChangeFunction;
    onSubmit: MouseEventHandler;
    onResetError: (key: keyof PasswordErrors) => FocusEventHandler;
    onToggleTwoFactor: ChangeFunction;
    newPassword: string;
    confirmPassword: string;
    password: string;
    loading: boolean;
    errors: PasswordErrors;
    twoFactorEnabled: boolean;
    allowDataUsage: boolean;
    onChangeAllowDataUsage: ChangeFunction<boolean>;
}

export default function PasswordSettings(props: Props) {
    const {t} = useTranslation();
    const {
        password,
        newPassword,
        confirmPassword,
        onChange,
        onResetError,
        errors,
        loading,
        onSubmit,
        twoFactorEnabled,
        onToggleTwoFactor,
        allowDataUsage,
        onChangeAllowDataUsage
    } = props;

    function renderFields() {
        return (
            <div className='fields'>
                <Loader active={loading} />
                <BluematePasswordInput
                    value={password}
                    onChange={onChange('pass')}
                    label={t('settings.password.currentPassword', 'Huidig wachtwoord *')}
                    error={errors.password}
                    onFocus={onResetError('password')}
                    inputProps={{placeholder: t('settings.password.currentPasswordPlaceholder', 'Huidig wachtwoord')}}
                />
                <BluematePasswordInput
                    value={newPassword}
                    onChange={onChange('new')}
                    label={t('settings.password.newPassword', 'Nieuw wachtwoord *')}
                    error={errors.newPassword}
                    onFocus={onResetError('newPassword')}
                    inputProps={{placeholder: t('settings.password.newPasswordPlaceholder', 'Nieuw wachtwoord')}}
                />
                <PasswordStrength value={newPassword} />
                <BluematePasswordInput
                    value={confirmPassword}
                    onChange={onChange('confirm')}
                    label={t('settings.password.confirmPassword', 'Bevestig wachtwoord *')}
                    error={errors.confirmPassword}
                    onFocus={onResetError('confirmPassword')}
                    inputProps={{placeholder: t('settings.password.confirmPasswordPlaceholder', 'Bevestig wachtwoord')}}
                />
            </div>
        );
    }

    function renderPassword() {
        return (
            <div className='change-password-container'>
                <div className='header-container'>
                    <label className='settings-title'>
                        {t('settings.password.changeTitle', 'Wachtwoord wijzigen')}
                    </label>
                    <label className='settings-sub'>
                        {t(
                            'settings.password.changeSub',
                            'We raden sterk aan om iedere 60 dagen je wachtwoord te wijzigen'
                        )}
                    </label>
                </div>
                {renderFields()}
                <BluemateButton onClick={onSubmit} disabled={loading} label={t('settings.password.save', 'Opslaan')} />
            </div>
        );
    }

    function renderRightContainer() {
        return (
            <div className='right-container'>
                {render2Fa()}
                {renderDataUsage()}
            </div>
        );
    }

    function render2Fa() {
        return (
            <div>
                <div className='header-container'>
                    <label className='settings-title'>
                        {t('settings.password.twoFactorTitle', 'Tweestapsverificatie')}
                    </label>
                    <label className='settings-sub'>
                        {t(
                            'settings.password.twoFactorSub',
                            'Met tweestapsverificatie voeg je een extra beveiligingslaag toe aan je account.'
                        )}
                    </label>
                </div>
                <BluemateSwitch
                    value={twoFactorEnabled}
                    onChange={onToggleTwoFactor}
                    mainLabel={t('settings.password.twoFactorTitle', 'Tweestapsverificatie')}
                    labels={[t('settings.password.off', 'UIT'), t('settings.password.on', 'AAN')]}
                />
            </div>
        );
    }

    function renderDataUsage() {
        return (
            <div>
                <div className='header-container'>
                    <label className='settings-title'>
                        {t('settings.password.dataUsageTitle', 'Data gebruiken voor verbetering')}
                    </label>
                    <label className='settings-sub'>
                        {t(
                            'settings.password.dataUsageSub',
                            'Hier geef je aan of het ons is toegestaan jouw data mee te nemen om onze Product Research ' +
                                'inschattingen te verbeteren. Jouw exacte verkoopcijfers worden nooit getoond aan anderen.'
                        )}
                    </label>
                </div>
                <BluemateSwitch
                    value={allowDataUsage}
                    onChange={onChangeAllowDataUsage}
                    mainLabel={t('settings.password.dataUsageToggleTitle', 'Verbeteren toegestaan')}
                    labels={[t('settings.password.off', 'UIT'), t('settings.password.on', 'AAN')]}
                />
            </div>
        );
    }

    return (
        <div className='security-settings-page'>
            <InfoBanner
                featureKey='security_settings'
                variant='info'
                title={t('settings.password.bannerTitle', 'Beveiliging')}
                showForNewUsersOnly={true}
            >
                {t(
                    'settings.password.bannerBody',
                    'We raden aan tweestapsverificatie in te schakelen. Je hoeft dan ook je e-mail niet meer te verifieren.'
                )}
            </InfoBanner>

            <Loader active={loading} />

            <div className='security-settings'>
                {renderPassword()}
                {renderRightContainer()}
            </div>
        </div>
    );
}
